import { useEffect, useState } from "react";

/**
 * useKeyboardOpen — true when the on-screen keyboard is showing on a
 * mobile browser. Detected via VisualViewport API:
 *   • visualViewport.height shrinks when the keyboard opens.
 *   • threshold of 150px (keyboards are always >150px tall).
 *
 * Used by Layout to HIDE the floating bottom chrome (mobile bottom nav,
 * bug-report button, deploy-version pill) while typing. Without this,
 * `position: fixed; bottom: 0` elements anchor to the LAYOUT viewport
 * not the VISUAL viewport — they end up behind / under the keyboard,
 * then occasionally redraw stale mid-screen when the keyboard closes.
 *
 * Iter 290zo (2026-06-03). Cause of the "nav stuck mid-screen" bug
 * reported with screenshot showing nav + bug button + version pill all
 * at the same wrong y-coordinate.
 */
export default function useKeyboardOpen(thresholdPx = 150) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = null;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        // Difference between layout viewport (window.innerHeight) and
        // visual viewport. > thresholdPx means the keyboard is up.
        const diff = window.innerHeight - vv.height;
        setOpen(diff > thresholdPx);
      });
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("orientationchange", update);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [thresholdPx]);
  return open;
}
